import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, RefreshCw, LogOut, ArrowLeft, Plus, Trash2, Pencil, X, Star } from "lucide-react";
import {
  login, fetchMe, fetchEnquiries, fetchStats, updateEnquiryStatus,
  fetchPackages, createPackage, updatePackage, deletePackage,
} from "../lib/api";
import { ProductsTab } from "../components/admin/ProductsTab";
import { OrdersTab } from "../components/admin/OrdersTab";
import { SettingsTab } from "../components/admin/SettingsTab";

const STATUSES = ["new", "contacted", "closed"];
const statusStyle = {
  new: "text-gold border-goldline",
  contacted: "text-blue-300 border-blue-900",
  closed: "text-[#6f6c64] border-[#26241f]",
};
const TOKEN_KEY = "sb_token";

/* ---------------- Login ---------------- */
const Login = ({ onAuth }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await login(email, password);
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuth(data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#26241f] py-3 text-[#f4f2ec] placeholder:text-[#5c584f] focus:border-gold focus:outline-none transition-colors";

  return (
    <div className="min-h-screen grid place-items-center px-6 bg-[#0a0a09] text-[#f4f2ec]" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="w-12 h-12 border border-goldline grid place-items-center mb-8">
          <Lock size={20} className="text-gold" />
        </div>
        <p className="eyebrow mb-4">The Scent Butler</p>
        <h1 className="font-serif text-4xl text-[#f4f2ec] mb-8">Admin Login</h1>
        <form onSubmit={submit} className="space-y-6">
          <input type="email" data-testid="admin-email-input" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input type="password" data-testid="admin-password-input" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" disabled={loading} data-testid="admin-login-button" className="w-full px-8 py-4 bg-gold text-[#050505] text-xs uppercase tracking-[0.2em] hover:bg-[#e6c56a] transition-colors disabled:opacity-60">
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <Link to="/" className="inline-flex items-center gap-2 text-[#6f6c64] text-sm mt-8 hover:text-gold transition-colors">
          <ArrowLeft size={14} /> Back to site
        </Link>
      </div>
    </div>
  );
};

/* ---------------- Package editor modal ---------------- */
const emptyPkg = { name: "", price: "", period: "/month", description: "", features: [""], featured: false, order: 0 };

const PackageModal = ({ pkg, token, onClose, onSaved }) => {
  const [form, setForm] = useState(pkg || emptyPkg);
  const [saving, setSaving] = useState(false);
  const editing = !!pkg?.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFeature = (i, v) => setForm((f) => ({ ...f, features: f.features.map((x, idx) => (idx === i ? v : x)) }));
  const addFeature = () => setForm((f) => ({ ...f, features: [...f.features, ""] }));
  const removeFeature = (i) => setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Name and price are required.");
    setSaving(true);
    const payload = { ...form, features: form.features.filter((x) => x.trim()), order: Number(form.order) || 0 };
    try {
      if (editing) await updatePackage(pkg.id, payload, token);
      else await createPackage(payload, token);
      toast.success(editing ? "Package updated." : "Package created.");
      onSaved();
      onClose();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-[#26241f] px-4 py-3 text-[#f4f2ec] placeholder:text-[#5c584f] focus:border-gold focus:outline-none transition-colors text-sm";

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm grid place-items-center px-4 overflow-y-auto py-10" data-testid="package-modal">
      <div className="w-full max-w-lg bg-[#0a0a09] border border-[#26241f] p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl text-[#f4f2ec]">{editing ? "Edit Package" : "New Package"}</h3>
          <button onClick={onClose} className="text-[#9a968c] hover:text-gold" data-testid="modal-close"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input className={inputCls} placeholder="Name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pkg-name" />
            <input className={inputCls} placeholder="Order (0,1,2)" value={form.order} onChange={(e) => set("order", e.target.value)} data-testid="pkg-order" />
            <input className={inputCls} placeholder="Price ($89 / Custom)" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="pkg-price" />
            <input className={inputCls} placeholder="Period (/month)" value={form.period} onChange={(e) => set("period", e.target.value)} data-testid="pkg-period" />
          </div>
          <textarea className={`${inputCls} resize-none`} rows={2} placeholder="Description" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="pkg-description" />

          <div>
            <p className="eyebrow text-[0.6rem] mb-3">Features</p>
            <div className="space-y-2">
              {form.features.map((f, i) => (
                <div key={i} className="flex gap-2">
                  <input className={inputCls} placeholder={`Feature ${i + 1}`} value={f} onChange={(e) => setFeature(i, e.target.value)} data-testid={`pkg-feature-${i}`} />
                  <button onClick={() => removeFeature(i)} className="text-[#6f6c64] hover:text-red-400 px-2"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <button onClick={addFeature} className="mt-3 text-gold text-xs uppercase tracking-[0.15em] inline-flex items-center gap-1" data-testid="pkg-add-feature"><Plus size={13} /> Add feature</button>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#c8a24a] w-4 h-4" data-testid="pkg-featured" />
            <span className="text-sm text-[#cfccc4]">Mark as "Most Popular"</span>
          </label>

          <button onClick={save} disabled={saving} className="w-full px-6 py-4 bg-gold text-[#050505] text-xs uppercase tracking-[0.2em] hover:bg-[#e6c56a] transition-colors disabled:opacity-60 mt-2" data-testid="pkg-save">
            {saving ? "Saving…" : editing ? "Update Package" : "Create Package"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Packages tab ---------------- */
const PackagesTab = ({ token }) => {
  const [packages, setPackages] = useState([]);
  const [modal, setModal] = useState(null); // {} for new, pkg for edit

  const load = useCallback(() => {
    fetchPackages().then((r) => setPackages(r.data)).catch(() => toast.error("Failed to load packages."));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await deletePackage(id, token);
      toast.success("Package deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="packages-tab">
      <div className="flex items-center justify-between mb-8">
        <p className="text-sm text-[#9a968c]">Manage the pricing packages shown on the Information page.</p>
        <button onClick={() => setModal({})} className="inline-flex items-center gap-2 px-5 py-3 bg-gold text-[#050505] text-xs uppercase tracking-[0.15em] hover:bg-[#e6c56a] transition-colors" data-testid="add-package-button">
          <Plus size={14} /> New Package
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {packages.map((p) => (
          <div key={p.id} className={`p-8 border ${p.featured ? "border-goldline bg-[#0c0b08]" : "border-[#1a1814]"}`} data-testid={`admin-package-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                {p.featured && <span className="inline-flex items-center gap-1 eyebrow text-[0.55rem]"><Star size={10} /> Popular</span>}
                <h3 className="font-serif text-xl text-[#f4f2ec] mt-1">{p.name}</h3>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setModal(p)} className="p-2 text-[#9a968c] hover:text-gold transition-colors" data-testid={`edit-package-${p.id}`}><Pencil size={15} /></button>
                <button onClick={() => remove(p.id)} className="p-2 text-[#9a968c] hover:text-red-400 transition-colors" data-testid={`delete-package-${p.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
            <p className="font-serif text-3xl text-gold mt-3">{p.price}<span className="text-sm text-[#9a968c]">{p.period}</span></p>
            <p className="text-sm text-[#9a968c] mt-3">{p.description}</p>
            <ul className="mt-4 space-y-2">
              {(p.features || []).map((f) => <li key={f} className="text-xs text-[#cfccc4]">— {f}</li>)}
            </ul>
          </div>
        ))}
      </div>

      {modal && <PackageModal pkg={modal.id ? modal : null} token={token} onClose={() => setModal(null)} onSaved={load} />}
    </div>
  );
};

/* ---------------- Enquiries tab ---------------- */
const EnquiriesTab = ({ token }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s] = await Promise.all([fetchEnquiries(token), fetchStats(token)]);
      setEnquiries(e.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load enquiries.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await updateEnquiryStatus(id, status, token);
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      load();
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Update failed.");
    }
  };

  return (
    <div data-testid="enquiries-tab">
      <div className="flex justify-end mb-6">
        <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 px-5 py-3 border border-[#26241f] text-[#cfccc4] text-xs uppercase tracking-[0.15em] hover:border-goldline hover:text-gold transition-colors">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1a1814] mb-12 border border-[#1a1814]">
        {[{ label: "Total", value: stats.total }, { label: "New", value: stats.new }, { label: "Contacted", value: stats.contacted }, { label: "Closed", value: stats.closed }].map((s) => (
          <div key={s.label} className="bg-[#050505] p-8" data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="font-serif text-4xl text-gold">{s.value}</div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#9a968c] mt-2">{s.label}</p>
          </div>
        ))}
      </div>

      {enquiries.length === 0 ? (
        <div className="border border-[#1a1814] p-16 text-center text-[#9a968c]">No enquiries yet.</div>
      ) : (
        <div className="border border-[#1a1814] overflow-x-auto">
          <table className="w-full text-left" data-testid="enquiries-table">
            <thead>
              <tr className="border-b border-[#1a1814] text-[#6f6c64] text-xs uppercase tracking-[0.15em]">
                <th className="p-5 font-normal">Name</th>
                <th className="p-5 font-normal">Company</th>
                <th className="p-5 font-normal">Space</th>
                <th className="p-5 font-normal">Details</th>
                <th className="p-5 font-normal">Message</th>
                <th className="p-5 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {enquiries.map((e) => (
                <tr key={e.id} className="border-b border-[#12110e] hover:bg-[#0a0a09] transition-colors align-top" data-testid={`enquiry-row-${e.id}`}>
                  <td className="p-5">
                    <p className="text-[#f4f2ec]">{e.name}</p>
                    <a href={`mailto:${e.email}`} className="text-xs text-gold hover:underline">{e.email}</a>
                    {e.phone && <p className="text-xs text-[#6f6c64] mt-1">{e.phone}</p>}
                  </td>
                  <td className="p-5 text-[#cfccc4] text-sm">{e.company || "—"}</td>
                  <td className="p-5 text-[#cfccc4] text-sm">
                    <p>{e.space_type || e.industry || "—"}</p>
                    {e.space_size && <p className="text-xs text-[#6f6c64] mt-1">{e.space_size}</p>}
                    {e.spaces && <p className="text-xs text-[#6f6c64]">{e.spaces}</p>}
                  </td>
                  <td className="p-5 text-[#cfccc4] text-sm">
                    {e.timeline && <p className="text-xs text-[#9a968c]">⏱ {e.timeline}</p>}
                    {e.budget && <p className="text-xs text-[#9a968c] mt-1">$ {e.budget}</p>}
                    {e.position && <p className="text-xs text-[#6f6c64] mt-1">{e.position}</p>}
                    {!e.timeline && !e.budget && !e.position && "—"}
                  </td>
                  <td className="p-5 text-[#9a968c] text-sm max-w-xs">{e.message}</td>
                  <td className="p-5">
                    <div className="flex flex-col gap-1">
                      {STATUSES.map((st) => (
                        <button key={st} onClick={() => changeStatus(e.id, st)} data-testid={`status-${st}-${e.id}`}
                          className={`text-[0.65rem] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${e.status === st ? statusStyle[st] : "text-[#4a473f] border-transparent hover:text-[#9a968c]"}`}>
                          {st}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ---------------- Dashboard ---------------- */
export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    if (token && !authed) {
      fetchMe(token)
        .then(() => setAuthed(true))
        .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); })
        .finally(() => setChecking(false));
    }
  }, [token, authed]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setToken("");
  };

  if (checking) return <div className="min-h-screen grid place-items-center bg-[#0a0a09] text-[#9a968c]">Loading…</div>;
  if (!authed) return <Login onAuth={(t) => { setToken(t); setAuthed(true); }} />;

  return (
    <div className="min-h-screen pt-12 pb-24 bg-[#0a0a09] text-[#f4f2ec]" data-testid="admin-dashboard">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow mb-2">The Scent Butler</p>
            <h1 className="font-serif text-4xl text-[#f4f2ec]">Dashboard</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-5 py-3 border border-[#26241f] text-[#cfccc4] text-xs uppercase tracking-[0.15em] hover:border-goldline hover:text-gold transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>

        <div className="flex flex-wrap gap-8 border-b border-[#1a1814] mb-12">
          {[
            { id: "orders", label: "Orders" },
            { id: "products", label: "Products" },
            { id: "settings", label: "Store settings" },
            { id: "enquiries", label: "Enquiries" },
            { id: "packages", label: "Packages" },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`pb-4 text-sm uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors ${tab === t.id ? "border-gold text-gold" : "border-transparent text-[#b3aea4] hover:text-[#f4f2ec]"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "orders" && <OrdersTab token={token} />}
        {tab === "products" && <ProductsTab token={token} />}
        {tab === "settings" && <SettingsTab token={token} />}
        {tab === "enquiries" && <EnquiriesTab token={token} />}
        {tab === "packages" && <PackagesTab token={token} />}
      </div>
    </div>
  );
}
